// Bohaterowie scenek (krok 3) — „biblia postaci” do generatora obrazów i przypisanie głosów (SPEC §21.6, §22.4, §22.11).
// Każda postać ma stały głos: głos główny („main” — dotychczasowy głos lektora ElevenLabs, czyta też kroki 1–2 i karty) albo drugi
// („second” — nowy głos ElevenLabs, który dodaje właściciel; id w voice.config.json → secondVoiceId). Głos zależy od POSTACI.
// Uczeń (dziecko lub dorosły) gra ZAWSZE drugą postać (Emma), a pierwszy mówi zawsze Tom (SPEC §22.4).
// Drugi głos jest KOBIECY (decyzja 17.10: ElevenLabs XfNU2rGpBa01ckF309OY), więc druga postać to kobieta — Emma (wcześniej Andrew, mężczyzna);
// po polsku jej kwestie i zwroty do niej mają formy żeńskie („jestem głodna”, „droga Emmo”). Tom: głos męski, mężczyzna.
// Tom i Emma to „dwie osoby bez wieku”: aplikacja uczy dzieci i dorosłych, więc scenka ma pole `cast` — domyślnie dorośli,
// dzieci tam, gdzie temat tego chce (szkoła, zabawa). Wygląd (`look`) dobiera generator obrazów według `cast`.

export type VoiceSlot = 'main' | 'second';

/** Kto jest na obrazku scenki: dorośli (domyślnie) albo dzieci. */
export type Cast = 'adult' | 'child';
export const CASTS: readonly Cast[] = ['adult', 'child'];

export interface Character {
  name: string;
  /** Opis wyglądu dorosłego do promptu obrazka — niezmienny w każdej lekcji z `cast: adult`. */
  look: string;
  /** Ta sama postać jako dziecko (lekcje z `cast: child`); brak = jak `look`. */
  lookChild?: string;
  voice: VoiceSlot;
}

export const CHARACTERS: readonly Character[] = [
  { name: 'Tom', look: 'a man about thirty-five with short dark hair and a green shirt', lookChild: 'a boy about ten years old with short dark hair and a green t-shirt', voice: 'main' },
  { name: 'Emma', look: 'a woman about thirty-five with curly light-brown hair and a blue sweater', lookChild: 'a girl about ten years old with curly light-brown hair and a blue sweater', voice: 'second' },
];

export type Gender = 'f' | 'm';
/** Płeć postaci z opisu wyglądu (woman/girl → kobieta). Pilnuje spójności opisów obrazków i polskich form w dialogach (SPEC §23.15). */
export const genderOf = (c: Character): Gender => (/\b(woman|girl)\b/i.test(c.look) ? 'f' : 'm');

export const characterOf = (name: string): Character | undefined => CHARACTERS.find((c) => c.name.toLowerCase() === name.toLowerCase());

/** Wygląd postaci w danym składzie sceny. */
export const lookOf = (c: Character, cast: Cast = 'adult'): string => (cast === 'child' ? (c.lookChild ?? c.look) : c.look);

/** Głos postaci; nieznane imię → głos główny. */
export const voiceOf = (name: string | undefined): VoiceSlot => (name ? (characterOf(name)?.voice ?? 'main') : 'main');

/**
 * Rola dziecka w scence (patch-50): chłopiec gra postać męską (Tom), dziewczynka — żeńską (Emma), żeby dziecko mówiło kwestie „swojej” postaci.
 * Dialogi są pisane tak, że uczeń to druga postać (Emma; `scene.learner`), więc to rola ustawiana w czasie sesji, a nie w treści. Brak płci w profilu
 * (konto sprzed patcha-31, tryb lokalny) albo brak postaci tej płci w dialogu = rola z lekcji. `child`: 'm' | 'f' | ''.
 */
export function roleFor(learner: string, speakers: readonly string[], child: string): string {
  if (child !== 'm' && child !== 'f') return learner;
  const hit = speakers.find((name) => {
    const c = characterOf(name);
    return c !== undefined && genderOf(c) === child;
  });
  return hit ?? learner;
}
